import { Component, OnInit, computed, effect, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbService, SiteService } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { SiteCompanyService } from '../../../services/site-company.service';
import { BankTransactionProxyService } from '../../../proxies/bank-transaction.proxy.service';
import { BankAccountProxyService, BankAccountSearchCommand } from '../../../proxies/bank-account.proxy.service';
import {
  FINANCIAL_ACCOUNT_ENTITY_CODE,
  FINANCIAL_COMPANY_ENTITY_CODE,
  BankFee,
  BankFeeType,
  BankInstrumentType,
  BankTransaction,
  BankTransactionMovementType,
  BankTransactionStatus,
} from '../../../models/bank-transaction/bank-transaction';
import {
  BankCounterLineFormGroup,
  BankFeeFormGroup,
  BankTransactionFormGroup,
} from '../../../models/bank-transaction/bank-transaction-form';
import {
  BankMovementControls,
  BankMovementMode,
  PreviewLine,
  netMovementAmount,
} from '../../../models/bank-transaction/bank-movement-form';
import { BankMovementOption } from '../../shared/bank-movement-form/bank-movement-form.component';
import { BankAccount, BankAccountStatus } from '../../../models/bank-account/bank-account';
import { PostingModule } from '../../../models/financial-account/financial-account';
import { EnumService } from '../../../services/enum.service';
import { TranslateService } from '@ngx-translate/core';
import { reportInvalidFormFields } from '@app/utilities';
import { switchMap } from 'rxjs';

interface EnumOption {
  label: string;
  value: number;
}

/**
 * Create or edit a manual bank movement — money in or money out of one of our accounts (#5076).
 *
 * <p>Deliberately not a two-endpoint form. An incoming or outgoing movement touches exactly one of our
 * accounts; the other side is a GL account, not a second bank. The entity still stores a From/To pair, so
 * both endpoints are derived on save from the single amount and the direction.</p>
 *
 * <p>Direction is fixed at creation and shown as a chip. Changing it mid-edit would silently swap which
 * side of every ledger account the movement lands on, so the form offers Cancel instead.</p>
 */
@Component({
  selector: 'app-bank-transaction-details',
  templateUrl: './bank-transaction-details.component.html',
  styleUrl: './bank-transaction-details.component.scss',
})
export class BankTransactionDetailsComponent implements OnInit {
  readonly BankTransactionStatus = BankTransactionStatus;
  readonly BankTransactionMovementType = BankTransactionMovementType;
  readonly PostingModule = PostingModule;

  /** A signal, not a plain field: `isNew` is a computed over it, and a computed over a mutable field
   *  never recomputes — it caches its first value forever. That made every save after the first look
   *  like a create. */
  readonly id = signal<string | null>(null);
  readonly isLoading = signal(0);
  readonly saving = signal(false);

  readonly status = signal<number>(BankTransactionStatus.DRAFT);
  readonly movementType = signal<number>(BankTransactionMovementType.INCOME);

  /** The account this movement hits. Its currency is the movement's currency — there is no second one. */
  readonly bankAccount = signal<BankAccount | null>(null);
  readonly bankAccountOptions = signal<BankAccount[]>([]);

  readonly journalEntryReference = signal<string | null>(null);

  private currencyLabels: Record<number, string> = {};
  private movementLabels: Record<number, string> = {};

  readonly isNew = computed(() => this.id() === null || this.id() === 'new');
  readonly isIncome = computed(() => this.movementType() === BankTransactionMovementType.INCOME);
  readonly isEditable = computed(() => this.status() === BankTransactionStatus.DRAFT);
  readonly showAccounting = computed(() => !!this.journalEntryReference());

  /** Currency is the account's, never a choice — a movement cannot be in a currency the account isn't in. */
  readonly currencyLabel = computed(() => {
    const account = this.bankAccount();
    return account ? (this.currencyLabels[account.currency] ?? '') : '';
  });

  readonly directionLabel = computed(() => this.movementLabels[this.movementType()] ?? '');
  readonly directionSeverity = computed<'success' | 'danger'>(() => (this.isIncome() ? 'success' : 'danger'));

  fieldLabels: Record<keyof BankTransactionFormGroup, string> = {
    number: 'BankTransaction_Details_Form_Field_Number',
    transactionDate: 'BankTransaction_Details_Form_Field_TransactionDate',
    bankingReference: 'BankTransaction_Details_Form_Field_BankingReference',
    bankAccountCode: 'BankTransaction_Details_Form_Field_BankAccount',
    amount: 'BankTransaction_Details_Form_Field_Amount',
    concept: 'BankTransaction_Details_Form_Field_Concept',
    notes: 'BankTransaction_Details_Form_Field_Notes',
    glCounterAccountLines: 'BankTransaction_Details_Form_Field_CounterAccount',
    instrumentType: 'BankTransaction_Details_Form_Field_InstrumentType',
    endToEndId: 'BankTransaction_Details_Form_Field_EndToEndId',
    uetr: 'BankTransaction_Details_Form_Field_Uetr',
    chargeBearer: 'BankTransaction_Details_Form_Field_ChargeBearer',
    traceNumber: 'BankTransaction_Details_Form_Field_TraceNumber',
    fees: 'BankTransaction_Details_Form_Field_Fees',
  };

  readonly form = new FormGroup<BankTransactionFormGroup>({
    number: new FormControl<string | null>({ value: null, disabled: true }),
    transactionDate: new FormControl<Date | null>(null, [Validators.required]),
    bankingReference: new FormControl<string | null>(null),
    bankAccountCode: new FormControl<string | null>(null, [Validators.required]),
    amount: new FormControl<number | null>(null, [Validators.required, Validators.min(0.01)]),
    concept: new FormControl<string | null>(null),
    notes: new FormControl<string | null>(null),
    glCounterAccountLines: new FormArray<FormGroup<BankCounterLineFormGroup>>([]),
    instrumentType: new FormControl<number>(BankInstrumentType.BANK_INITIATED, { nonNullable: true }),
    endToEndId: new FormControl<string | null>(null),
    uetr: new FormControl<string | null>(null),
    chargeBearer: new FormControl<string | null>(null),
    traceNumber: new FormControl<string | null>(null),
    fees: new FormArray<FormGroup<BankFeeFormGroup>>([]),
  });

  /**
   * Only the types the API accepts. CHEQUE, CASHIERS_CHEQUE, CASH and CARD exist in the enum so their
   * codes never renumber, but the allow-list rejects them — offering one would produce a form that
   * validates cleanly and fails on save.
   */
  readonly instrumentTypeOptions = signal<BankMovementOption[]>([
    { label: 'Bank initiated', labelKey: 'BankInstrumentType_BANK_INITIATED', value: BankInstrumentType.BANK_INITIATED },
    { label: 'Wire', labelKey: 'BankInstrumentType_WIRE', value: BankInstrumentType.WIRE },
    { label: 'ACH', labelKey: 'BankInstrumentType_ACH', value: BankInstrumentType.ACH },
    { label: 'Transfer', labelKey: 'BankInstrumentType_TRANSFER', value: BankInstrumentType.TRANSFER },
    { label: 'Deposit', labelKey: 'BankInstrumentType_DEPOSIT', value: BankInstrumentType.DEPOSIT },
    { label: 'Unspecified', labelKey: 'BankInstrumentType_UNSPECIFIED', value: BankInstrumentType.UNSPECIFIED },
  ]);

  readonly feeTypeOptions = signal<EnumOption[]>([
    { label: 'Bank fee', value: BankFeeType.BANK },
    { label: 'Security fee', value: BankFeeType.SECURITY },
  ]);

  // ---- Amounts ----
  private readonly amountSig = signal<number | null>(null);
  private readonly feesSig = signal<BankFee[]>([]);

  /** The breakdown as plain values, so the entry preview recomputes while the user types. */
  private readonly counterLinesSig = signal<{ reference: string | null; number: string | null; name: string | null; amount: number }[]>([]);

  /** What the breakdown adds up to — the same figure the shared form shows under the table. */
  readonly counterTotal = computed(() => this.counterLinesSig().reduce((sum, l) => sum + l.amount, 0));

  /** True when the breakdown matches the movement. The API refuses a post otherwise, so post() checks it. */
  readonly counterBalanced = computed(() => Math.abs((this.amountSig() ?? 0) - this.counterTotal()) < 0.005);

  readonly totalFees = computed(() => this.feesSig().reduce((sum, f) => sum + (f.amount || 0), 0));

  /** Which situation the shared movement form is rendering. Fixed at creation, like the direction it follows. */
  readonly movementMode = computed<BankMovementMode>(() => (this.isIncome() ? 'movement-in' : 'movement-out'));

  /** What actually moves the bank balance — the same figure the shared form shows as its net row. */
  readonly netAmount = computed(() => netMovementAmount(this.movementMode(), this.amountSig() ?? 0, this.totalFees()));

  /**
   * The host's form controls, mapped onto the shape the shared movement form drives. A standalone movement
   * has every section: an instrument, a memo, and an offsetting account the user picks.
   */
  readonly movementControls: BankMovementControls = {
    amount: this.form.controls.amount,
    date: this.form.controls.transactionDate,
    statementReference: this.form.controls.bankingReference,
    memo: this.form.controls.concept,
    instrument: {
      type: this.form.controls.instrumentType,
      endToEndId: this.form.controls.endToEndId,
      uetr: this.form.controls.uetr,
      chargeBearer: this.form.controls.chargeBearer,
      traceNumber: this.form.controls.traceNumber,
    },
    counterAccount: this.form.controls.glCounterAccountLines,
    fees: this.form.controls.fees,
  };

  /**
   * The counter account is the other half of the entry, so posting needs one. Movements spawned by an AP
   * payment or cash funding arrive with their entry already booked and are exempt.
   */
  readonly requiresCounterAccount = computed(() => !this.journalEntryReference());

  /**
   * The entry as it will be posted. Built here rather than fetched so it updates as the user types — the
   * balance line is how they notice a missing charge before submitting rather than after.
   *
   * The fee accounts are config-driven on the API, so the UI cannot know their numbers; they are shown by
   * type and resolved server-side.
   */
  readonly previewLines = computed<PreviewLine[]>(() => {
    const account = this.bankAccount();
    const gross = this.amountSig() ?? 0;
    if (!account || gross <= 0) return [];

    const bankLine: PreviewLine = {
      accountNumber: account.glAccountNumber ?? null,
      accountName: `${account.bankName} (${'Bank'})`,
      debit: this.isIncome() ? this.netAmount() : 0,
      credit: this.isIncome() ? 0 : this.netAmount(),
    };

    // One preview line per account the breakdown names. Reading the signal rather than the FormArray is
    // what makes the preview redraw as the user types — a computed cannot see a control it never read.
    const counterLines: PreviewLine[] = this.counterLinesSig().map((line) => ({
      accountNumber: line.number,
      accountName: line.name ?? '—',
      debit: this.isIncome() ? 0 : line.amount,
      credit: this.isIncome() ? line.amount : 0,
    }));

    const feeLines: PreviewLine[] = this.feesSig()
      .filter((f) => (f.amount || 0) > 0)
      .map((f) => ({
        accountNumber: null,
        accountName: this.feeTypeOptions().find((o) => o.value === f.type)?.label ?? '',
        debit: f.amount,
        credit: 0,
      }));

    return this.isIncome() ? [bankLine, ...feeLines, ...counterLines] : [...counterLines, ...feeLines, bankLine];
  });

  /** The FinancialCompany behind the selected site, resolved once. A site id is NOT a company id — posting
   *  one as the company reference makes every downstream lookup (accounting period, chart of accounts) miss,
   *  because it is searching for a company that does not exist. */
  private readonly companyReference = signal<string | null>(null);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: BankTransactionProxyService,
    private bankAccountService: BankAccountProxyService,
    private enumService: EnumService,
    private translate: TranslateService,
    private siteCompany: SiteCompanyService,
    private breadcrumbService: BreadcrumbService,
  ) {
    // The account resolves asynchronously, so the trail is fed as it arrives rather than declared on the
    // route: rooted at the account because that is the screen people open a movement from, and Cancel
    // returns there. A static route crumb sent them back to a list they had never been through.
    effect(
      () => {
        const account = this.bankAccount();
        if (!account) return;
        const name = account.bankName || account.name || '';
        this.breadcrumbService.set([
          { label: account.code ? `${name} (${account.code})` : name, command: () => this.goToAccount() },
          { label: 'BankTransaction_Details_Breadcrumb_Transactions', route: '/bank-transactions' },
          { label: 'BankTransaction_Details_Breadcrumb_Details' },
        ]);
      },
      { allowSignalWrites: true },
    );

    this.currencyLabels = this.enumService.getEnum('Currency').values as Record<number, string>;
    this.movementLabels = this.enumService.getEnum('BankTransactionMovementType').values as Record<number, string>;

    this.form.controls.amount.valueChanges.subscribe((v) => this.amountSig.set(v));
    this.form.controls.fees.valueChanges.subscribe(() => this.syncFees());
    this.form.controls.glCounterAccountLines.valueChanges.subscribe(() => this.syncCounterLines());

    this.siteCompany.getFinancialCompanyId(SiteService.getSelectedSiteId()).subscribe((companyId) => this.companyReference.set(companyId));
  }

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      const id = params.get('id')?.trim();
      if (!id) {
        this.router.navigate(['/bank-transactions']);
        return;
      }
      this.id.set(id);

      if (id === 'new') {
        this.movementType.set(this.parseMovementType(params.get('type')));
        this.form.controls.transactionDate.setValue(new Date());
        this.loadBankAccounts(params.get('account'));
      } else {
        this.loadById();
      }
    });
  }

  // ---- Bank account ----

  private loadBankAccounts(preselectCode: string | null): void {
    this.isLoading.update((n) => n + 1);
    const command: BankAccountSearchCommand = { statuses: [BankAccountStatus.ACTIVE] };
    this.bankAccountService.search(command, 1, 200, true).subscribe({
      next: (response) => {
        const accounts = response.data?.items ?? [];
        this.bankAccountOptions.set(accounts);
        const chosen = preselectCode ? accounts.find((a) => a.code === preselectCode) : undefined;
        if (chosen) this.selectBankAccount(chosen);
        this.isLoading.update((n) => n - 1);
      },
      error: () => {
        this.bankAccountOptions.set([]);
        this.isLoading.update((n) => n - 1);
      },
    });
  }

  selectBankAccount(account: BankAccount | null): void {
    this.bankAccount.set(account);
    this.form.controls.bankAccountCode.setValue(account?.code ?? null);
  }

  onBankAccountChange(code: string | null): void {
    this.selectBankAccount(this.bankAccountOptions().find((a) => a.code === code) ?? null);
  }

  // ---- Fees ----

  get feeRows(): FormArray<FormGroup<BankFeeFormGroup>> {
    return this.form.controls.fees;
  }

  get counterLineRows(): FormArray<FormGroup<BankCounterLineFormGroup>> {
    return this.form.controls.glCounterAccountLines;
  }

  /** Mirrors the fee rows into a signal so the entry preview recomputes as they are typed. Adding and
   *  removing rows belongs to the shared movement form, which owns the table. */
  /** One breakdown row. The account number rides along so the row and the entry preview can label
   *  themselves without re-fetching the chart of accounts. */
  private newCounterLine(
    reference?: string | null,
    accountNumber?: string | null,
    amount?: number | null,
    description?: string | null,
  ): FormGroup<BankCounterLineFormGroup> {
    return new FormGroup<BankCounterLineFormGroup>({
      reference: new FormControl<string | null>(reference ?? null),
      number: new FormControl<string | null>(accountNumber ?? null),
      name: new FormControl<string | null>(null),
      amount: new FormControl<number | null>(amount ?? null, [Validators.min(0.01)]),
      description: new FormControl<string | null>(description ?? null),
    });
  }

  private syncCounterLines(): void {
    this.counterLinesSig.set(
      this.counterLineRows.controls.map((g) => ({
        reference: g.controls.reference.value,
        number: g.controls.number.value,
        name: g.controls.name.value,
        amount: g.controls.amount.value ?? 0,
      })),
    );
  }

  private syncFees(): void {
    this.feesSig.set(
      this.feeRows.controls.map((g) => ({
        type: g.controls.type.value as BankFeeType,
        amount: g.controls.amount.value ?? 0,
      })),
    );
  }

  private parseMovementType(raw: string | null): number {
    return (raw ?? '').toUpperCase() === 'OUTCOME' ? BankTransactionMovementType.OUTCOME : BankTransactionMovementType.INCOME;
  }

  // ---- Load ----

  private loadById(): void {
    this.isLoading.update((n) => n + 1);
    this.service.getById(this.id()!).subscribe({
      next: (response) => {
        const data = response.data;
        if (data) this.hydrate(data);
        this.isLoading.update((n) => n - 1);
      },
      error: (error) => {
        this.isLoading.update((n) => n - 1);
        console.error('Error fetching bank transaction:', error);
      },
    });
  }

  private hydrate(data: BankTransaction): void {
    this.status.set(data.status);
    this.movementType.set(data.financialTransactionType);
    this.journalEntryReference.set(data.journalEntryReference?.reference ?? null);

    const isIncome = data.financialTransactionType === BankTransactionMovementType.INCOME;
    const ours = isIncome ? data.to : data.from;

    this.form.patchValue({
      number: data.number,
      transactionDate: data.transactionDate ? new Date(data.transactionDate) : null,
      bankingReference: data.bankingReference,
      bankAccountCode: ours?.code ?? null,
      amount: ours?.amount ?? null,
      concept: data.concept,
      notes: data.notes,
      instrumentType: data.instrument?.type ?? BankInstrumentType.BANK_INITIATED,
      endToEndId: data.instrument?.endToEndId ?? null,
      uetr: data.instrument?.uetr ?? null,
      chargeBearer: data.instrument?.chargeBearer ?? null,
      traceNumber: data.instrument?.traceNumber ?? null,
    });

    this.counterLineRows.clear();
    for (const line of data.glCounterAccountLines ?? []) {
      this.counterLineRows.push(this.newCounterLine(line.account?.reference, line.account?.number, line.amount, line.description));
    }

    this.feeRows.clear();
    for (const fee of data.fees ?? []) {
      this.feeRows.push(
        new FormGroup<BankFeeFormGroup>({
          type: new FormControl<number>(fee.type, { nonNullable: true }),
          amount: new FormControl<number | null>(fee.amount, [Validators.min(0.01)]),
        }),
      );
    }

    if (ours?.code) this.resolveAccount(ours.code);
    if (this.status() !== BankTransactionStatus.DRAFT) this.form.disable();
  }

  private resolveAccount(code: string): void {
    const command: BankAccountSearchCommand = { code, statuses: [BankAccountStatus.ACTIVE] };
    this.bankAccountService.search(command, 1, 5, true).subscribe({
      next: (response) => {
        const items = response.data?.items ?? [];
        this.bankAccount.set(items.find((a) => a.code === code) ?? items[0] ?? null);
      },
      error: () => this.bankAccount.set(null),
    });
  }

  // ---- Save / post ----

  private buildPayload(): BankTransaction {
    const v = this.form.getRawValue();
    const account = this.bankAccount();
    const currency = account?.currency ?? 0;
    const amount = v.amount ?? 0;
    const isIncome = this.isIncome();

    // The entity keeps a From/To pair; this screen has one account, so the side that isn't ours carries a
    // null code. Both endpoints hold the gross — charges live in `fees` and never change these.
    const ourEndpoint = { code: v.bankAccountCode, amount, currency };
    const otherEndpoint = { code: null, amount, currency };

    return {
      financialTransactionType: this.movementType(),
      instrument: {
        type: v.instrumentType,
        // Empty strings become null: the API treats '' as absent, but null is what the field means.
        endToEndId: v.endToEndId || null,
        uetr: v.uetr || null,
        chargeBearer: v.chargeBearer || null,
        traceNumber: v.traceNumber || null,
        // Not captured here: an ACH effective date is a scheduling instruction you give when
        // ORIGINATING a payment, and this screen records movements that have already happened.
        effectiveDate: null,
      },
      transactionDate: (v.transactionDate ?? new Date()).toISOString(),
      postingDate: (v.transactionDate ?? new Date()).toISOString(),
      fiscalYear: (v.transactionDate ?? new Date()).getFullYear(),
      fiscalPeriod: (v.transactionDate ?? new Date()).getMonth() + 1,
      financialCompanyReference: {
        reference: this.companyReference() ?? '',
        entityDefinitionCode: FINANCIAL_COMPANY_ENTITY_CODE,
      },
      concept: v.concept,
      bankingReference: v.bankingReference,
      description: v.concept,
      notes: v.notes,
      fees: this.feesSig().filter((f) => f.amount > 0),
      // Rows with no account yet are dropped rather than sent as blanks: a half-typed draft is the normal
      // state of this form, and the API refuses a line with no account.
      glCounterAccountLines: this.counterLineRows.controls
        .filter((g) => !!g.controls.reference.value)
        .map((g) => ({
          account: {
            reference: g.controls.reference.value!,
            number: g.controls.number.value,
            entityDefinitionCode: FINANCIAL_ACCOUNT_ENTITY_CODE,
          },
          amount: g.controls.amount.value ?? 0,
          description: g.controls.description.value,
        })),
      status: BankTransactionStatus.DRAFT,
      from: isIncome ? otherEndpoint : ourEndpoint,
      to: isIncome ? ourEndpoint : otherEndpoint,
    } as BankTransaction;
  }

  saveDraft(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return;
    }
    if (!this.companyReference()) {
      console.error('No financial company resolved for the selected site; cannot save bank transaction.');
      return;
    }

    this.saving.set(true);
    this.isLoading.update((n) => n + 1);
    const payload = this.buildPayload();
    const request = this.isNew() ? this.service.createDraft(payload) : this.service.updateDraft(this.id()!, payload);

    request.subscribe({
      next: (response) => {
        this.saving.set(false);
        this.isLoading.update((n) => n - 1);
        if (response.data?.id) {
          this.id.set(response.data.id);
          this.router.navigate([], { queryParams: { id: this.id() }, replaceUrl: true, queryParamsHandling: 'merge' });
        }
      },
      error: (error) => {
        this.saving.set(false);
        this.isLoading.update((n) => n - 1);
        console.error('Error saving draft:', error);
      },
    });
  }

  /**
   * Saves what is on screen, then posts what was saved.
   *
   * <p>Posting used to call the endpoint directly, which meant the API booked the last SAVED draft while
   * the user was looking at their unsaved edits. The offsetting account was the usual casualty: picked in
   * the form, never sent, and the post rejected for a field the screen showed as filled in.</p>
   */
  post(): void {
    if (this.isNew()) return;

    // Both enforced at post, not as standing validators: the API only demands a balanced breakdown when the
    // entry is actually booked, so requiring it earlier would block saving a half-finished draft — which is
    // most of this form's life, since the second account is typed after the first.
    const lines = this.counterLineRows;
    const named = lines.controls.filter((g) => !!g.controls.reference.value);

    if (this.requiresCounterAccount() && named.length === 0) {
      lines.markAllAsTouched();
      GlobalMessageUtil.warn(
        this.translate.instant('BankTransaction_CounterAccount_Required_Title'),
        this.translate.instant('BankTransaction_CounterAccount_Required'),
        5000,
      );
      return;
    }

    // The same arithmetic the API refuses on, said here while it can still be fixed in place rather than
    // coming back as a server error after submitting.
    if (this.requiresCounterAccount() && !this.counterBalanced()) {
      lines.markAllAsTouched();
      GlobalMessageUtil.warn(
        this.translate.instant('BankTransaction_CounterAccount_Unbalanced_Title'),
        this.translate.instant('BankTransaction_CounterAccount_Unbalanced', {
          total: this.counterTotal().toFixed(2),
          amount: (this.amountSig() ?? 0).toFixed(2),
        }),
        5000,
      );
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      reportInvalidFormFields(this.form, this.fieldLabels, this.translate);
      return;
    }

    this.saving.set(true);
    this.isLoading.update((n) => n + 1);

    this.service
      .updateDraft(this.id()!, this.buildPayload())
      .pipe(switchMap(() => this.service.post(this.id()!)))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          // Back to the ACCOUNT whose balance just moved, not the list of all accounts. Posting is the
          // moment the figure on that page changes, so it is the one place the result of this action is
          // visible — and it is the screen the movement was opened from. Same destination as Cancel, so
          // both ways out of this form land somewhere the user recognizes.
          this.goToAccount();
        },
        error: (error) => {
          this.saving.set(false);
          this.isLoading.update((n) => n - 1);
          console.error('Error posting transaction:', error);
        },
      });
  }

  /**
   * Back to the account this movement belongs to, which is the screen it was opened from — the account page
   * lists its movements and carries the button that starts one. Falls back to the all-transactions list only
   * when the account is not resolved yet, which is the one case where there is nowhere better to go.
   *
   * <p>Cancel deliberately lands on the FIRST breadcrumb rather than one level up. A movement's parent
   * really is its bank account; the transactions list is a cross-account index that nobody navigates
   * through to reach a specific movement.</p>
   */
  cancel(): void {
    this.goToAccount();
  }

  private goToAccount(): void {
    const accountId = this.bankAccount()?.id;
    if (accountId) {
      this.router.navigate(['/bank-account-details'], { queryParams: { id: accountId } });
      return;
    }
    this.router.navigate(['/bank-transactions']);
  }
}
